import { useId, useState } from 'react';
import { FormField, Select, TextField } from '../../components';
import { LocalePicker } from '../locales/LocalePicker';
import {
  CAMPAIGN_CHANNELS,
  CAMPAIGN_TYPES,
  type CampaignChannel,
  type CampaignInput,
  type CampaignPatch,
  type CampaignType,
} from './api';
import { useSourceSuggestions } from './queries';
import { CHANNEL_LABEL, TYPE_LABEL, Typeahead, sanitizeToken, slugError, targetUrlError, useDebounced } from './parts';

/**
 * The campaign's own fields, for create and edit. The campaign ID is typed once
 * and never changes; it is also utm_campaign. Source (utm_source) defaults to
 * homeplated and suggests what other campaigns use. The target URL is typed
 * without any utm_* — every short link adds them when it is opened.
 */

export interface CampaignDraft {
  name: string;
  slug: string;
  type: CampaignType | '';
  channel: CampaignChannel | '';
  localeId: string;
  targetUrl: string;
  utmSource: string;
  utmTerm: string;
}

export function emptyCampaignDraft(slugSeed: string): CampaignDraft {
  return {
    name: '',
    slug: slugSeed,
    type: '',
    channel: '',
    localeId: '',
    targetUrl: '',
    utmSource: 'homeplated',
    utmTerm: '',
  };
}

export function campaignDraftErrors(d: CampaignDraft, opts: { editing?: boolean } = {}): Record<string, string> {
  const e: Record<string, string> = {};
  if (!d.name.trim()) e.name = 'Name the campaign.';
  if (!opts.editing) {
    const s = slugError(d.slug);
    if (s) e.slug = s;
  }
  if (!d.type) e.type = 'Choose a type.';
  if (!d.channel) e.channel = 'Choose a channel.';
  if (!d.localeId) e.localeId = 'Choose a locale.';
  const t = targetUrlError(d.targetUrl);
  if (t) e.targetUrl = t;
  if (!d.utmSource) e.utmSource = 'A source is required.';
  return e;
}

export function draftToInput(d: CampaignDraft): CampaignInput {
  return {
    slug: d.slug,
    name: d.name.trim(),
    type: d.type as CampaignType,
    channel: d.channel as CampaignChannel,
    localeId: d.localeId,
    targetUrl: d.targetUrl.trim(),
    utmSource: d.utmSource,
    utmTerm: d.utmTerm || undefined,
  };
}

/** Only the fields that changed, for PATCH. */
export function draftToPatch(before: CampaignDraft, after: CampaignDraft): CampaignPatch {
  const p: CampaignPatch = {};
  if (after.name.trim() !== before.name) p.name = after.name.trim();
  if (after.type !== before.type) p.type = after.type as CampaignType;
  if (after.channel !== before.channel) p.channel = after.channel as CampaignChannel;
  if (after.localeId !== before.localeId) p.localeId = after.localeId;
  if (after.targetUrl.trim() !== before.targetUrl) p.targetUrl = after.targetUrl.trim();
  if (after.utmSource !== before.utmSource) p.utmSource = after.utmSource;
  if (after.utmTerm !== before.utmTerm) p.utmTerm = after.utmTerm;
  return p;
}

const FIXED_HINT = 'Fixed — the campaign has traffic.';

export function CampaignFields({
  draft,
  onChange,
  errors = {},
  editing = false,
  reportingFixed = false,
  localeName,
}: {
  draft: CampaignDraft;
  onChange: (next: CampaignDraft) => void;
  errors?: Record<string, string>;
  /** The campaign ID cannot change once saved. */
  editing?: boolean;
  /** The campaign has traffic: type, channel, locale, source and term are what it is reported under. */
  reportingFixed?: boolean;
  /** Shown for the fixed locale. */
  localeName?: string;
}) {
  const id = useId();
  const set = (patch: Partial<CampaignDraft>) => onChange({ ...draft, ...patch });
  const [sourceOpened, setSourceOpened] = useState(false);
  const sourceTerm = useDebounced(draft.utmSource);
  const sources = useSourceSuggestions(sourceOpened ? sourceTerm : '');

  return (
    <div className="grid gap-3.5">
      <div className="grid grid-cols-2 gap-3.5">
        <FormField label="Campaign name" htmlFor={`${id}-name`} required error={errors.name}>
          <TextField
            id={`${id}-name`}
            surface="white"
            value={draft.name}
            placeholder="Kermés Unidos por Nuestras Raíces"
            onChange={(e) => set({ name: e.target.value })}
            invalid={!!errors.name}
          />
        </FormField>
        <FormField
          label="Campaign ID"
          htmlFor={`${id}-slug`}
          required={!editing}
          error={errors.slug}
          hint={editing ? 'Fixed once saved.' : 'Lowercase, digits and _, ending _MMDDYY. Cannot change once saved.'}
        >
          <TextField
            id={`${id}-slug`}
            surface="white"
            value={draft.slug}
            readOnly={editing}
            className="font-mono"
            placeholder="kermes_092026"
            onChange={(e) => set({ slug: sanitizeToken(e.target.value) })}
            invalid={!!errors.slug}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-3.5">
        <FormField
          label="Type"
          htmlFor={`${id}-type`}
          required
          error={errors.type}
          hint={reportingFixed ? FIXED_HINT : undefined}
        >
          <Select
            id={`${id}-type`}
            surface="white"
            disabled={reportingFixed}
            placeholder="Choose…"
            value={draft.type}
            options={CAMPAIGN_TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
            onChange={(e) => set({ type: e.target.value as CampaignType })}
          />
        </FormField>
        <FormField
          label="Channel"
          htmlFor={`${id}-channel`}
          required
          error={errors.channel}
          hint={reportingFixed ? FIXED_HINT : 'Also utm_medium.'}
        >
          <Select
            id={`${id}-channel`}
            surface="white"
            disabled={reportingFixed}
            placeholder="Choose…"
            value={draft.channel}
            options={CAMPAIGN_CHANNELS.map((c) => ({ value: c, label: CHANNEL_LABEL[c] }))}
            onChange={(e) => set({ channel: e.target.value as CampaignChannel })}
          />
        </FormField>
      </div>

      {reportingFixed ? (
        <FormField label="Locale" htmlFor={`${id}-locale`} hint={FIXED_HINT}>
          <TextField id={`${id}-locale`} surface="white" value={localeName || draft.localeId} readOnly />
        </FormField>
      ) : (
        <FormField label="Locale" required error={errors.localeId}>
          <LocalePicker
            value={draft.localeId ? [draft.localeId] : []}
            onChange={(ids) => set({ localeId: ids[ids.length - 1] ?? '' })}
            placeholder="Choose a locale"
          />
        </FormField>
      )}
      <FormField
        label="Target URL"
        htmlFor={`${id}-target`}
        required
        error={errors.targetUrl}
        hint="Where people land. No utm_ parameters — links add them. Can be changed later; links and printed codes follow."
      >
        <TextField
          id={`${id}-target`}
          surface="white"
          type="url"
          value={draft.targetUrl}
          placeholder="https://app.homeplated.com/"
          onChange={(e) => set({ targetUrl: e.target.value })}
          invalid={!!errors.targetUrl}
        />
      </FormField>

      <div className="grid grid-cols-2 gap-3.5">
        <FormField
          label="Source"
          htmlFor={`${id}-source`}
          required
          error={errors.utmSource}
          hint={reportingFixed ? FIXED_HINT : 'utm_source. Connection codes always report homeplated.'}
        >
          {reportingFixed ? (
            <TextField id={`${id}-source`} surface="white" className="font-mono" value={draft.utmSource} readOnly />
          ) : (
            <Typeahead
              id={`${id}-source`}
              freeText
              value={draft.utmSource}
              sanitize={sanitizeToken}
              onTermChange={(v) => {
                setSourceOpened(true);
                set({ utmSource: v });
              }}
              onPick={(o) => set({ utmSource: o.value })}
              options={(sources.data ?? []).filter((s) => s !== draft.utmSource).map((s) => ({ value: s, label: s }))}
              placeholder="homeplated"
              invalid={!!errors.utmSource}
            />
          )}
        </FormField>
        <FormField
          label="Term"
          htmlFor={`${id}-term`}
          optional
          hint={reportingFixed ? FIXED_HINT : 'utm_term. Lowercase, digits and _.'}
        >
          <TextField
            id={`${id}-term`}
            surface="white"
            readOnly={reportingFixed}
            value={draft.utmTerm}
            className="font-mono"
            onChange={(e) => set({ utmTerm: sanitizeToken(e.target.value) })}
          />
        </FormField>
      </div>
    </div>
  );
}
